import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader
} from '@bliks-io/ui';
import type { PublishValidationError } from '@bliks-io/contracts';
import {
  buildPerStepIssues,
  getStepLabel,
  type PublishErrorEventSummary,
  type PublishErrorStepSummary
} from '../publish/publishErrorFormatting';

interface Props {
  error: PublishValidationError;
  stepsById: Map<string, PublishErrorStepSummary>;
  eventsById?: Map<string, PublishErrorEventSummary>;
  onClose: () => void;
  onGoToStep: (stepId: string) => void;
}

export function PublishErrorDialog({ error, stepsById, eventsById, onClose, onGoToStep }: Props) {
  const { t } = useTranslation();
  const { perStep, processLevel } = buildPerStepIssues(error, t, stepsById, eventsById);

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogShell size="sm">
        <DialogShellHeader
          icon={<AlertCircle className="w-5 h-5 text-danger" />}
          iconTone="danger"
          title={t('processDraft.publishErrors.title')}
          onClose={onClose}
        />
        <DialogShellBody>
          {processLevel.length > 0 && (
            <div className="border border-line rounded-md p-3">
              <div className="text-sm font-medium text-foreground mb-2">
                {t('processDraft.publishErrors.processLevel')}
              </div>
              <ul className="space-y-1 text-sm">
                {processLevel.map((msg, i) => (
                  <li key={`p-${i}`} className="text-warning">
                    • {msg}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {Array.from(perStep.entries()).map(([stepId, errors]) => (
            <div key={stepId} className="border border-line rounded-md p-3">
              <div className="flex items-center justify-between mb-2 gap-2">
                <span className="text-sm font-medium text-foreground truncate">
                  {getStepLabel(stepId, stepsById, t, eventsById)}
                </span>
                <button
                  onClick={() => onGoToStep(stepId)}
                  className="text-xs text-info hover:underline shrink-0"
                >
                  {t('processDraft.publishErrors.goToStep')}
                </button>
              </div>
              <ul className="space-y-1 text-sm">
                {errors.content.map((msg, i) => (
                  <li key={`c-${i}`} className="text-danger">
                    • {msg}
                  </li>
                ))}
                {errors.graph.map((msg, i) => (
                  <li key={`g-${i}`} className="text-warning">
                    • {msg}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </DialogShellBody>
        <DialogShellFooter secondary={{ label: t('common.close'), onClick: onClose }} />
      </DialogShell>
    </Dialog>
  );
}
